/**
 * UnifiedAuthContext — v2.0.7
 * 
 * Simplificado: agora consome auth do ChatwootContext (auto-provision via PostMessage).
 * Mantém interface useAuth() para compatibilidade com todos os componentes existentes.
 */
import React, { createContext, useContext, useEffect, ReactNode, useCallback } from 'react';
import { useChatwoot } from './ChatwootContext';

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  tenantId?: string | null;
  ultimoLogin?: string | null;
  criadoEm: string;
}

interface UnifiedAuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isCheckingAuth: boolean;
  authLevel: 'iframe' | 'query' | 'traditional' | null;
  isUrlLogin: boolean;
  isSuperAdmin: boolean;
  login: (email: string, senha: string, fromUrl?: boolean, silent?: boolean) => Promise<boolean>;
  logout: (silent?: boolean) => void;
  checkAuth: () => Promise<boolean>;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType | undefined>(undefined);

interface UnifiedAuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = '/api';

export const UnifiedAuthProvider: React.FC<UnifiedAuthProviderProps> = ({ children }) => {
  const chatwoot = useChatwoot();
  
  // Derivar estado de auth do ChatwootContext
  const user = chatwoot.user as User | null;
  const token = chatwoot.token;
  const isAuthenticated = chatwoot.isAuthenticated;
  const isSuperAdmin = chatwoot.isSuperAdmin;
  
  // Loading: estamos no iframe e ainda não recebemos auth
  const isLoading = chatwoot.isInChatwoot && chatwoot.isLoading;
  const isCheckingAuth = chatwoot.isInChatwoot && !chatwoot.isAuthReady;
  
  // Sempre iframe mode quando estamos no Chatwoot
  const isUrlLogin = chatwoot.isInChatwoot;
  const authLevel: 'iframe' | null = chatwoot.isInChatwoot ? 'iframe' : null;

  /**
   * Login — mantido para compatibilidade, mas não usado em modo iframe
   */
  const login = useCallback(async (_email: string, _senha: string, _fromUrl?: boolean, _silent?: boolean): Promise<boolean> => {
    // console.warn('⚠️ [Auth] login() chamado mas auth é via PostMessage agora');
    return false;
  }, []);

  /**
   * Logout — limpa tokens locais
   */
  const logout = useCallback((_silent?: boolean) => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('url_login');
    localStorage.removeItem('auth_level');
  }, []);

  /**
   * Check auth — verifica se o token ainda é válido
   */
  const checkAuth = useCallback(async (): Promise<boolean> => {
    if (!token) return false;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      return response.ok;
    } catch {
      return false;
    }
  }, [token]);

  // Setters mantidos para compatibilidade
  const setUser = useCallback((_user: User | null) => {}, []);
  const setToken = useCallback((_token: string) => {}, []);

  /**
   * Interceptor 401: quando API retorna 401, solicitar re-provision
   */
  useEffect(() => {
    if (!chatwoot.isInChatwoot || !isAuthenticated) return;

    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      const url = args[0]?.toString() || '';
      // Se 401 em rota da API (mas NÃO no auto-provision), re-provisionar
      if (
        response.status === 401 &&
        url.includes('/api/') &&
        !url.includes('/auto-provision')
      ) {
        // console.log('🔄 [Auth] Token expirado (401), solicitando re-provision...');
        chatwoot.reProvision();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, chatwoot.isInChatwoot, chatwoot.reProvision]);

  const value: UnifiedAuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    isCheckingAuth,
    authLevel,
    isUrlLogin,
    isSuperAdmin,
    login,
    logout,
    checkAuth,
    setUser,
    setToken,
  };

  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

/**
 * Hook para consumir o contexto de autenticação unificada
 */
export const useUnifiedAuth = (): UnifiedAuthContextType => {
  const context = useContext(UnifiedAuthContext);
  if (context === undefined) {
    throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider');
  }
  return context;
};
